/**
 * The register's column contract and the one rule that merges a custom
 * module's columns into the core's (SA-453 §3, D2).
 *
 * Pure and separate from `RegisterView` so the replacement rules are tested
 * without rendering a table. A module column names a CORE column id it takes
 * the position of; one that names nothing, or an anchor the core is not
 * rendering (`status` is conditional), APPENDS — a module's work is never
 * silently dropped, and a core column rename degrades to a layout change
 * rather than a blank cell.
 *
 * SA-462 (workflow UX sweep spec §8.3–8.4, D12): a column also carries its
 * layout — a default width (a stored preference overrides it, clamped at
 * `minWidth`), an optional header group (consecutive equal groups span one
 * header cell), whether a string cell clamps, and whether a rule opens after
 * it. `columnWidth`, `boundaryBefore` and `headerRows` are the pure rules the
 * shell (`RegisterTable`) renders from.
 * SA-1124: a column may name the server sort key its header sorts by
 * (`sortKey`, see `registerSort.ts`); a column naming none has no sort button.
 * SA-1185: a module's table sorts in the browser: its column names any key,
 * the first click's direction (`sortFirst`) and the row's sort value
 * (`sortValue`, `clientSort.ts`), never the rendered cell.
 */
import type { ReactNode } from 'react'
import type { Order } from '@/types/commerce'
import type { ColumnSortKey, SortDir } from './registerSort'
import type { SortValue } from './clientSort'

/** Default widths in px by kind (spec §8.4). */
export const DEFAULT_WIDTHS = { text: 160, numeric: 120, reference: 200, step: 220 } as const
/** The narrowest any column may be dragged or stored (spec §8.3). */
export const MIN_WIDTH = 72

/** A column a custom module contributes through the REGISTER_COLUMNS slot. */
export interface RegisterColumn {
  id: string
  header: string
  /** The header's tooltip (a `Tip`, never a native `title`). */
  title?: string
  align?: 'left' | 'right'
  /** A core column id this takes the position of; absent or unknown appends. */
  replaces?: string
  /** Default width in px; absent = the kind's default (text 160, numeric 120). */
  width?: number
  minWidth?: number
  /** A grouped header's label; consecutive equal groups span one cell. */
  group?: string
  /** The server sort key this header sorts by (`registerSort.COLUMN_SORTS`); absent = no sort button. */
  sortKey?: ColumnSortKey
  /** A PLAIN function, not a component: it may NOT use hooks. Its data comes
   *  from the provider's single read, through the closure. */
  Cell: (order: Order) => ReactNode
}

/** A column as the table renders it, core or custom. Generic over the row
 *  (SA-854): the register's rows are orders, a module's list its own. */
export interface MergedColumn<Row = Order> {
  id: string
  header: string
  /** The header's tooltip (a `Tip`, never a native `title`). */
  title?: string
  headerClassName: string
  cellClassName: string
  render: (row: Row) => ReactNode
  /** Default width in px; a stored preference overrides it (spec §8.3). */
  width: number
  /** The floor for a stored or dragged width; default MIN_WIDTH. */
  minWidth?: number
  /** A grouped header's label; consecutive equal groups span (spec §8.4). */
  group?: string
  /** When the cell returns a string, the shell wraps it in ClampedText. */
  clamp?: boolean
  /** The column after this one opens with a rule (the wireframe's `.gb`). */
  boundaryAfter?: boolean
  /** The key this header sorts by: a server key on the register (SA-1124),
   *  any key on a module's table (SA-1185); absent = no sort button. */
  sortKey?: string
  /** The first click's direction; absent = the register's own (`registerSort.firstClick`). */
  sortFirst?: SortDir
  /** The client sort's value for a row (SA-1185, `clientSort.ts`); never the rendered cell. */
  sortValue?: (row: Row) => SortValue
}

/** A register column (the built-in register's, a module view's on the same
 *  server read): its `sortKey` must be one the server accepts, or a click
 *  sends `sort=<typo>` and the read answers 400 (SA-1193). `MergedColumn`
 *  takes any key since SA-1185, for a module table's client sort. */
export type ServerSortColumn = Omit<MergedColumn, 'sortKey'> & { sortKey?: ColumnSortKey }

/** The wireframe's density (spec §8.3). */
export const CELL = 'px-2.5 py-2'
const NUMERIC = `${CELL} text-right tabular-nums whitespace-nowrap`

function fromCustom(column: RegisterColumn): MergedColumn {
  const right = column.align === 'right'
  return {
    id: column.id,
    header: column.header,
    title: column.title,
    headerClassName: right ? `${CELL} text-right` : CELL,
    cellClassName: right ? NUMERIC : CELL,
    render: column.Cell,
    width: column.width ?? DEFAULT_WIDTHS[right ? 'numeric' : 'text'],
    minWidth: column.minWidth,
    group: column.group,
    sortKey: column.sortKey,
    // A module's text cell is free text more often than not; a numeric one
    // is nowrap by class and never clamps.
    clamp: right ? undefined : true,
  }
}

export function mergeColumns(core: MergedColumn[], custom: RegisterColumn[] = []): MergedColumn[] {
  if (custom.length === 0) return [...core]
  const coreIds = new Set(core.map((column) => column.id))
  const atAnchor = new Map<string, MergedColumn[]>()
  const appended: MergedColumn[] = []
  for (const column of custom) {
    const anchor = column.replaces && coreIds.has(column.replaces) ? column.replaces : null
    if (anchor === null) {
      appended.push(fromCustom(column))
      continue
    }
    const group = atAnchor.get(anchor)
    if (group) group.push(fromCustom(column))
    else atAnchor.set(anchor, [fromCustom(column)])
  }
  const out: MergedColumn[] = []
  for (const column of core) {
    const replacement = atAnchor.get(column.id)
    if (replacement) out.push(...replacement)
    else out.push(column)
  }
  return [...out, ...appended]
}

/** The width a column renders at: the stored preference, else its default,
 *  never below its minimum — an older preference below a raised minimum
 *  must not crush the column. */
export function columnWidth<Row>(column: MergedColumn<Row>, stored?: number): number {
  return Math.max(column.minWidth ?? MIN_WIDTH, stored ?? column.width)
}

/** A rule opens before a column that starts a new group, and after one
 *  flagged `boundaryAfter` (spec §8.3 "Group boundaries"). */
export function boundaryBefore<Row>(columns: MergedColumn<Row>[], index: number): boolean {
  if (index === 0) return false
  const prev = columns[index - 1]
  const column = columns[index]
  if (prev.boundaryAfter) return true
  return !!column.group && column.group !== prev.group
}

export interface HeaderCell<Row = Order> {
  key: string
  column?: MergedColumn<Row>
  group?: string
  colSpan: number
  rowSpan: number
  ariaLabel?: string
  boundary: boolean
}

/** The header as rows of cells: one row when no column is grouped; else an
 *  ungrouped column spans two rows and a run of one group is one spanning
 *  cell over its columns' own headers in the second row. */
export function headerRows<Row>(columns: MergedColumn<Row>[]): HeaderCell<Row>[][] {
  const grouped = columns.some((c) => c.group)
  if (!grouped) {
    return [columns.map((column, i) => ({
      key: column.id, column, colSpan: 1, rowSpan: 1, boundary: boundaryBefore(columns, i),
    }))]
  }
  const first: HeaderCell<Row>[] = []
  const second: HeaderCell<Row>[] = []
  for (let i = 0; i < columns.length; i += 1) {
    const column = columns[i]
    const boundary = boundaryBefore(columns, i)
    if (!column.group) {
      first.push({ key: column.id, column, colSpan: 1, rowSpan: 2, boundary })
      continue
    }
    const last = first[first.length - 1]
    if (last?.group === column.group && !boundary) last.colSpan += 1
    else first.push({ key: `group:${column.group}:${column.id}`, group: column.group, colSpan: 1, rowSpan: 1, boundary })
    second.push({
      key: column.id, column, colSpan: 1, rowSpan: 1, boundary,
      ariaLabel: `${column.group}, ${column.header.toLowerCase()}`,
    })
  }
  return [first, second]
}
